/**
 * The row pipeline: collects tbody sections from the DOM, pending fragments and parked rows,
 * sorts them, windows the visible set, and renumbers ranks. Dispatches ds:afterSwap.
 *
 * Calls on sibling mixins via `this`: Filtering (isVisibleRow, computeVisibleTiers,
 * appendLeagueSortedSections, selectedFantasyPosition), MobileTableChrome (setupTierObserver,
 * refreshMobileTableChrome, updateShowMobileTierLabel, observeVisibleTierRows), GuestCta
 * (repositionGuestCta), UserLeague (selectedUserLeagueId, cachedLeagueData, updatePlayerRow),
 * ColumnVisibility (updateColumnVisibilityStyles), RowLoading (loading), CoreState (basePath,
 * isSmallScreen).
 */
import Alpine from '/js/alpine/core@3.14.9/alpine.esm.js';
import { getArrItemByPropValue } from './../../helpers/DsHelper.js';
import { dsEventBus } from './RankingsConstants.js';

export default () => ({
    sortColumn: 'dsValue',
    sortAsc: false,
    allSections: [],
    pendingFragments: [],
    hadPendingFragments: false,

    // Hidden player rows parked off-DOM, and the table element they were parked from
    _parkedRows: [],
    _lastTableEl: null,
    _tierSwapBound: false,

    initParkedRows() {
        // Hidden player rows get parked here, detached from the DOM, so table layout and
        // Alpine tree walks only ever cover the visible window (see sortSections).
        // _lastTableEl detects real table swaps, which invalidate the parked rows.
        this._parkedRows = [];
        this._lastTableEl = document.querySelector('#rankingsTable');
    },

    initSections() {
        // Set up visibility and sorting
        this.computeVisibleTiers();
        this.refreshSections();
        Alpine.nextTick(() => {
            this.refreshMobileTableChrome();
            // Correct the server-side guest CTA slot against client-only visibility rules
            this.repositionGuestCta();
        });
    },

    initAfterSwapListener() {
        if (!this._tierSwapBound) {
            this._tierSwapBound = true;
            dsEventBus.addEventListener('ds:afterSwap', () => {
                // Analysis-style cells can first enter the DOM here, via load-rows
                // fragments - the depth-change HTMX swap itself doesn't contain them
                this.updateColumnVisibilityStyles();
                Alpine.nextTick(() => this.observeVisibleTierRows());
            });
        }
    },

    hasPendingFragments() {
        return this.pendingFragments && this.pendingFragments.length > 0;
    },

    refreshSections() {
        // Get existing tbody elements from DOM. The guest CTA row is positioned
        // by repositionGuestCta() and must stay out of the sort pipeline.
        let allTbodies = Array.from(this.$refs.rankingsTable.querySelectorAll('tbody:not([data-cta-row])'));

        // Add tbody elements from pending fragments
        if (this.hasPendingFragments()) {
            this.hadPendingFragments = true;

            this.pendingFragments.forEach(item => {
                const fragmentTbodies = Array.from(item.fragment.querySelectorAll('tbody'));
                allTbodies = allTbodies.concat(fragmentTbodies);
            });

            // Clear pending fragments since we've processed them
            this.pendingFragments = [];

            // Fragment rows fetched after ensureDsValueForUserLeague ran its DOM pass still
            // carry server-computed values. Apply the cached league values before the sort
            // values below are read from their data attributes.
            const leaguePlayers = this.selectedUserLeagueId ? this.cachedLeagueData[this.selectedUserLeagueId]?.players : null;
            if (leaguePlayers) {
                allTbodies.forEach(tbody => {
                    if (!tbody.hasAttribute('data-player-row') || tbody.isConnected) {
                        return;
                    }
                    const leaguePlayer = getArrItemByPropValue(tbody.dataset.key, leaguePlayers, 'player_id', false);
                    if (leaguePlayer) {
                        this.updatePlayerRow(tbody, leaguePlayer);
                    }
                });
            }
        }

        // Rows parked off-DOM by sortSections rejoin the pipeline here. They come after
        // DOM and fetched rows so fresher duplicates win the dedupe below.
        if (this._parkedRows?.length) {
            allTbodies = allTbodies.concat(this._parkedRows);
        }

        // Failsafe - Drop duplicate player rows in case DOM rows and fetched batches overlap.
        // DOM rows come first and may already carry league values, so keep those.
        const seenPlayerKeys = new Set();
        allTbodies = allTbodies.filter(tbody => {
            if (!tbody.hasAttribute('data-player-row')) {
                return true;
            }
            const key = tbody.dataset.key;
            if (seenPlayerKeys.has(key)) {
                if (tbody.isConnected) {
                    tbody.remove();
                }
                return false;
            }
            seenPlayerKeys.add(key);
            return true;
        });

        // Now process all tbodies to extract sort values
        const _allSections = allTbodies.map(tbody => {
            const sortValues = {};
            let isPlayerRow = false;

            if (tbody.hasAttribute('data-tier-row')) {
                // If this is a tier row, we need to extract the tier information
                if (this.usesOverallTier() && tbody.dataset.tierType === 'overall') {
                    sortValues.tierOverall = tbody.dataset.tierNumber;
                } else if (!this.usesOverallTier() && tbody.dataset.tierType === 'positional') {
                    sortValues.tierPositional = tbody.dataset.tierNumber;
                }
            } else {
                isPlayerRow = true;
                sortValues.tierOverall = tbody.dataset.tierOverall ?? null;
                sortValues.tierPositional = tbody.dataset.tierPositional ?? null;
            }
            tbody.querySelectorAll('td[data-attribute]').forEach(td => {
                const key = td.dataset.attribute;
                sortValues[key] = td.dataset.value ?? null;
            });

            // Key for player rows is the player_id, for tier rows it's the tier_key
            let key = isPlayerRow ? tbody.dataset.key : tbody.dataset.tierKey;

            // While we are here, let's also cache the lowercased player name
            if (tbody._playerNameLowerCase === undefined && isPlayerRow && tbody.dataset.playerName) {
                tbody._playerNameLowerCase = tbody.dataset.playerName.toLowerCase();
            }

            return {
                key: key,
                el: tbody,
                sortValues: sortValues,
                isPlayerRow: isPlayerRow,
            };
        });
        this.allSections = _allSections;

        if (this.isSmallScreen) {
            Alpine.nextTick(() => {
                this.setupTierObserver();
            });
        }
    },

    get currentSortParam() {
        const s = this.sortAsc ? this.sortColumn : `-${this.sortColumn}`;
        return s;
    },

    sortBy(column, defaultAsc = false) {
        this.sortAsc = this.sortColumn === column ? !this.sortAsc : defaultAsc;
        this.sortColumn = column;
        this.refreshTable();
        this.updateShowMobileTierLabel();
        Alpine.nextTick(() => this.refreshMobileTableChrome());
    },

    getSortClass(column) {
        if (this.sortColumn === column) {
            return this.sortAsc ? 'sort-asc' : 'sort-desc';
        }
        return '';
    },

    getTierSortValueKey() {
        return this.usesOverallTier() ? 'tierOverall' : 'tierPositional';
    },

    sortSections() {
        const dir = this.sortAsc ? 1 : -1;

        // Use My League runs WASM client-side; PHP divider rows keep default-scoring tier numbers.
        // Rebuild dividers from player data-tier-overall/positional on each sort (see appendLeagueSortedSections).
        const shouldRebuildLeagueTierRows =
            this.selectedUserLeagueId && this.sortColumn.includes('dsValue') && this.basePath !== 'weekly-rankings';

        // If we are rebuilding league tier rows, filter out the existing PHP divider rows from the sort.
        const sectionsForSort = shouldRebuildLeagueTierRows
            ? this.allSections.filter(section => !section.el.hasAttribute('data-tier-row'))
            : this.allSections;

        // Run visibility again for the inserted pending fragments
        sectionsForSort.forEach(row => {
            row.el.classList.remove('hidden-row');
            row.el.style.display = this.isVisibleRow(row.el) ? '' : 'none';
        });

        // Sort by the actual sort column first so hidden-row order matches the table
        // (weekly 3D, dsValue, etc.). Capping on dsValue before this re-sort hid
        // players from All Positions even when they ranked inside the top 250.
        const sorted = this.rankingsSortSections(sectionsForSort, this.sortColumn, dir, this.getTierSortValueKey());

        if (this.selectedFantasyPosition === '') {
            let visibleCount = 0;
            sorted.forEach(row => {
                if (!row.el.hasAttribute('data-player-row') || row.el.style.display === 'none') {
                    return;
                }
                visibleCount += 1;
                if (visibleCount > 250) {
                    row.el.classList.add('hidden-row');
                }
            });
        }

        // Hidden player rows are parked off-DOM instead of display:none'd, so table
        // layout, style invalidation, and Alpine tree walks only cover the visible
        // window (~250 rows instead of ~1000). Tier divider rows always stay attached —
        // their visibility depends on visibleTiers, which is recomputed below, and
        // Alpine's x-show on them handles the toggle.
        const keepAttached = row =>
            !row.el.hasAttribute('data-player-row') ||
            (row.el.style.display !== 'none' && !row.el.classList.contains('hidden-row') && !row.el.classList.contains('search-hidden'));
        const expectedAttached = sorted.filter(keepAttached);

        // Position/team/rookie filters change row visibility but not row order. When the
        // rows that should be attached already match the DOM exactly, skip the full
        // detach/re-append - it invalidates the whole table and re-walks every moved
        // tbody's Alpine bindings, which is the bulk of the interaction cost in WebKit.
        const domSections = this.$refs.rankingsTable.querySelectorAll('tbody:not([data-cta-row])');
        const orderUnchanged =
            !shouldRebuildLeagueTierRows &&
            !this.hadPendingFragments &&
            domSections.length === expectedAttached.length &&
            expectedAttached.every((row, i) => row.el === domSections[i]);

        let frag = null;
        if (!orderUnchanged) {
            frag = document.createDocumentFragment();
            const parked = [];
            if (shouldRebuildLeagueTierRows) {
                this.appendLeagueSortedSections(frag, sorted, keepAttached, parked);
            } else {
                sorted.forEach(row => {
                    if (keepAttached(row)) {
                        frag.appendChild(row.el);
                    } else {
                        row.el.remove();
                        parked.push(row.el);
                    }
                });
            }
            this._parkedRows = parked;
        }

        // Renumber visible player ranks after the rows moved into the fragment - the
        // queries and text writes then hit detached nodes instead of the live table
        sorted
            .filter(
                row => row.el.hasAttribute('data-player-row') && row.el.style.display !== 'none' && !row.el.classList.contains('hidden-row')
            )
            .forEach((row, i) => {
                const rankSpan = row.el.querySelector('.rank-index');
                if (rankSpan) rankSpan.textContent = i + 1;
            });

        // With no frag (skip path) this recomputes against the live document
        this.computeVisibleTiers(frag);

        if (!orderUnchanged) {
            // Drop stale PHP dividers before append - otherwise empty Tier 1/2/3 stack at top.
            if (shouldRebuildLeagueTierRows) {
                this.$refs.rankingsTable.querySelectorAll('[data-tier-row]').forEach(row => row.remove());
            }

            this.$refs.rankingsTable.appendChild(frag);
        }

        // Dispatch event if we processed any pending fragments
        if (this.hadPendingFragments) {
            dsEventBus.dispatchEvent(new CustomEvent('ds:afterSwap'));
            this.hadPendingFragments = false;
        }
    },

    async refreshTable() {
        // If there is a fetch request in progress, wait until it completes before refreshing
        while (this.loading) {
            await new Promise(resolve => setTimeout(resolve, 25));
        }
        this.refreshSections();
        this.sortSections();
        this.repositionGuestCta();
    },

    rankingsSortSections(allSections, col, dir, tierSortKey) {
        const compareDsValue = (a, b) => {
            const valA = parseFloat(a.sortValues.dsValue) || 0;
            const valB = parseFloat(b.sortValues.dsValue) || 0;
            if (valA === valB) {
                return dir * ((parseFloat(a.sortValues.fantasy_points) || 0) - (parseFloat(b.sortValues.fantasy_points) || 0));
            }
            return dir * (valA - valB);
        };

        return [...allSections].sort((a, b) => {
            if (col === 'dsValue') {
                // Get tier info for both rows
                const isTierRowA = a.el.hasAttribute('data-tier-row');
                const isTierRowB = b.el.hasAttribute('data-tier-row');
                const isMultiPositionFilter = (this.selectedFantasyPosition || '').toUpperCase() === 'IDP';

                if (isMultiPositionFilter) {
                    if (isTierRowA && !isTierRowB) return -1;
                    if (!isTierRowA && isTierRowB) return 1;
                    if (isTierRowA && isTierRowB) return 0;
                    return compareDsValue(a, b);
                }

                // League player order follows 3D value. Tier bars are still rebuilt
                // from data-tier-overall / data-tier-positional after this sort.
                if (this.selectedUserLeagueId && this.basePath !== 'weekly-rankings') {
                    return compareDsValue(a, b);
                }

                // Primary: tier sort, opposite of dsValue direction
                const tierA = parseInt(a.sortValues[tierSortKey]) || 0;
                const tierB = parseInt(b.sortValues[tierSortKey]) || 0;
                if (tierA !== tierB) {
                    // Flip direction: if dsValue dir is ASC, tiers go DESC
                    return -dir * (tierA - tierB);
                }

                // Secondary: within the same tier, tier headers come first, then players
                if (isTierRowA && !isTierRowB) return -1;
                if (!isTierRowA && isTierRowB) return 1;

                // Tertiary: dsValue, uses dir normally (both are players or both are tier headers)
                // Tier rows don't participate in dsValue sorting
                if (isTierRowA && isTierRowB) {
                    // Both are tier rows - they're equal in this sort
                    return 0;
                }
                return compareDsValue(a, b);
            } else {
                let valA = a.sortValues[col] ?? null;
                let valB = b.sortValues[col] ?? null;

                // Push nulls to bottom regardless of direction
                if (valA === null && valB === null) return 0;
                if (valA === null) return 1;
                if (valB === null) return -1;

                // -- Strip non-numeric characters from values; applicable to columns like auction value etc. --
                valA = typeof valA === 'string' ? valA.replace(/[^0-9.-]/g, '') : valA;
                valB = typeof valB === 'string' ? valB.replace(/[^0-9.-]/g, '') : valB;

                const numA = parseFloat(valA);
                const numB = parseFloat(valB);
                const isNumeric = !isNaN(numA) && !isNaN(numB);

                return isNumeric ? dir * (numA - numB) : dir * valA.localeCompare(valB);
            }
        });
    },
});
